'use client'

import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { FormattedMessage, Tabs } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, pxToRem } from '@b-stock/fe-core/design-system'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import { PageTitle } from '@/buyer-portal/components/PageTitle'
import { ListingSearchView } from '@/components/listings/ListingSearchView'
import { HeaderAlignedPageWidth } from '@/components/PageWidth'
import KeyBuyerAccessBadge from '@/components/storefrontAccess/KeyBuyerAccessBadge'
import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useTabsUrlParams } from '@/helpers/useTabsUrlParams'
import { getSellerLogo } from '@/helpers/utils'

import AboutContent from './content/aboutContent'
import ConditionContent from './content/conditionContent'
import DeviceUnlockingTermsContent from './content/deviceUnlockingTermsContent'
import FaqContent from './content/faqContent'
import {
  isConditionTabEnabledForStorefront,
  isDeviceUnlockingTermsTabEnabledForStorefront,
  isLegacyAboutTabEnabledForStorefront,
  isLegacyFaqTabEnabledForStorefront,
  isShippingTabEnabledForStorefront,
} from './content/legacyAboutFaqTabsFromLd'
import ShippingContent from './content/shippingContent'
import { StorefrontPolicies } from './StorefrontPolicies'

const LogoAndTabBar = styled(HeaderAlignedPageWidth)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem 2rem;
  flex-wrap: wrap;
  /* The header (z-index: 27, above this row) no longer reserves clickable
     bottom padding on this route, so nothing sits over these tabs by design.
     Kept as a guard: a storefront without a logo makes this row short enough
     that any future header padding would swallow the click outright. */
  position: relative;
  z-index: 26;

  /* The tab strip scrolls itself (overflow-x: auto in fe-core's Tabs), but a flex item will not
     shrink below its content, so a seller with a long tab — "Device Unlocking Terms" — pushed the
     strip past the viewport instead. body is overflow-x: hidden, so the far tabs were unreachable
     on a phone rather than scrollable.

     On every child, not just the strip: a storefront with no logo has the strip as its first
     child, which is also why the rule below misses it there. */
  & > * {
    min-width: 0;
  }

  & > :nth-child(2) {
    margin-bottom: 0;
    flex: 1;
  }

  /* flex-wrap's line-breaking test uses each item's flex-basis (0 here, via
     flex: 1), not its shrunk final size, so a wide logo alone never trips a
     wrap — the tab strip just gets starved to whatever space is left over
     (as little as 33px on a wide-logo storefront). Giving it a min-width
     raises that hypothetical size enough that a wide logo forces the wrap
     onto its own row instead, while min-width: 0 above still lets it shrink
     for horizontal scrolling once there. Scoped to mobile: at desktop widths
     there's already room for both without squeezing. */
  @media ${Breakpoints.max.small} {
    & > :nth-child(2) {
      min-width: 12rem;
    }
  }
`

const SellerLogoImage = styled.img`
  height: ${pxToRem(62)};
`

/* With the Key Buyer badge, logo and badge share the row's first slot, so the tab strip stays the
   second child (the rules above) and the badge stays beside the logo when the strip wraps on a
   phone. Without it the logo is the first child as before. */
const LogoAndBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
`

export default function StorefrontTabs({
  storefront,
  cmsSellerProfile,
  showBadge = false,
}: {
  storefront: StoreFrontDto
  /** Prefetched CMS row for About/FAQ follow-up; keeps RSC + LD kill switch wired. */
  cmsSellerProfile?: CmsSellerProfile | null
  /** The AT&T access decision's Key Buyer badge; shown in the header, so on every tab. */
  showBadge?: boolean
}) {
  const { formatMessage } = useIntl()
  const { documents, name: storefrontName } = storefront
  const logoUrl = getSellerLogo(documents)
  const additionalTabsEnabled = useFlag(
    FeatureFlags.ENABLE_ABOUT_FAQ_BY_STOREFRONT_ID
  )
  const myAccountHpNav = useFlag(FeatureFlags.MY_ACCOUNT_HP_NAV)

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        title: <FormattedMessage id="Storefront.listings.tab" />,
        content: (
          <ListingSearchView excludeSavedSearch storefront={storefront} />
        ),
        key: 'listings',
      },
    ]

    const storefrontId = storefront._id

    /**
     * A tab shows when either source says so, the same rule About and FAQ already follow: the
     * LaunchDarkly allowlist keeps the sellers who have these tabs today, and the CMS toggle adds
     * the ones an editor turns on. Which content renders is decided inside each tab.
     */
    const cmsTabEnabled = (enabled: boolean | null | undefined) =>
      typeof storefrontId === 'string' &&
      storefrontId.length > 0 &&
      enabled === true

    const legacyAboutEnabled = isLegacyAboutTabEnabledForStorefront(
      storefrontId,
      additionalTabsEnabled
    )

    const legacyFaqEnabled = isLegacyFaqTabEnabledForStorefront(
      storefrontId,
      additionalTabsEnabled
    )

    /**
     * Listings first, then the tabs in the order a buyer works through them: who the seller is,
     * then the questions they ask, then the detail. Push order is display order, so this block
     * is the source of truth for it — changing it moves the tabs.
     */
    if (legacyAboutEnabled || cmsTabEnabled(cmsSellerProfile?.aboutEnabled)) {
      baseTabs.push({
        title: <FormattedMessage id="Storefront.about.tab" />,
        content: (
          <AboutContent
            cmsSellerProfile={cmsSellerProfile}
            legacyCostcoStyleAboutEnabled={legacyAboutEnabled}
            sellerSlug={storefront.slug}
          />
        ),
        key: 'about',
      })
    }

    if (legacyFaqEnabled || cmsTabEnabled(cmsSellerProfile?.faqEnabled)) {
      baseTabs.push({
        title: <FormattedMessage id="Storefront.faq.tab" />,
        content: (
          <FaqContent
            cmsSellerProfile={cmsSellerProfile}
            legacyCostcoStyleFaqEnabled={legacyFaqEnabled}
            myAccountHpNav={myAccountHpNav}
            sellerSlug={storefront.slug}
            storefrontName={storefrontName}
          />
        ),
        key: 'faq',
      })
    }

    if (
      isConditionTabEnabledForStorefront(storefrontId, additionalTabsEnabled) ||
      cmsTabEnabled(cmsSellerProfile?.conditionsEnabled)
    ) {
      baseTabs.push({
        title: <FormattedMessage id="Storefront.condition.tab" />,
        content: <ConditionContent cmsSellerProfile={cmsSellerProfile} />,
        key: 'condition',
      })
    }

    if (
      isShippingTabEnabledForStorefront(storefrontId, additionalTabsEnabled) ||
      cmsTabEnabled(cmsSellerProfile?.shippingEnabled)
    ) {
      baseTabs.push({
        title: <FormattedMessage id="Storefront.shipping.tab" />,
        content: <ShippingContent cmsSellerProfile={cmsSellerProfile} />,
        key: 'shipping',
      })
    }

    if (
      isDeviceUnlockingTermsTabEnabledForStorefront(
        storefrontId,
        additionalTabsEnabled
      ) ||
      cmsTabEnabled(cmsSellerProfile?.deviceUnlockingEnabled)
    ) {
      baseTabs.push({
        title: <FormattedMessage id="Storefront.deviceUnlockingTerms.tab" />,
        content: (
          <DeviceUnlockingTermsContent cmsSellerProfile={cmsSellerProfile} />
        ),
        key: 'device-unlocking-terms',
      })
    }

    return baseTabs
  }, [
    storefront,
    storefrontName,
    additionalTabsEnabled,
    cmsSellerProfile,
    myAccountHpNav,
  ])

  const { tab, updateTabUrlParam } = useTabsUrlParams()
  const { trackButtonClicked } = useAnalytics()

  const setActiveTabKey = (newTab: string) => {
    if (newTab === 'about') {
      trackButtonClicked({
        buttonName: 'About_Seller_Storefront',
      })
    } else if (newTab === 'faq') {
      trackButtonClicked({
        buttonName: 'FAQ_Storefront',
      })
    } else if (newTab === 'condition') {
      trackButtonClicked({
        buttonName: 'Condition_Storefront',
      })
    } else if (newTab === 'shipping') {
      trackButtonClicked({
        buttonName: 'Shipping_Storefront',
      })
    } else if (newTab === 'device-unlocking-terms') {
      trackButtonClicked({
        buttonName: 'Device_Unlocking_Terms_Storefront',
      })
    }

    updateTabUrlParam(newTab)
  }

  const activeTabKey =
    (tabs.some(({ key }) => tab === key) && tab) || 'listings'

  const tabProps = {
    tabs,
    activeTabKey,
    setActiveTabKey,
  }

  const logo = logoUrl ? (
    <SellerLogoImage
      alt={formatMessage({ id: 'Storefront.header.logo' }, { storefrontName })}
      data-testid="storefront-logo"
      src={logoUrl}
    />
  ) : null

  return (
    <div data-testid="storefront">
      <PageTitle value={['PageTitle.seller', storefrontName]} />
      <LogoAndTabBar data-testid="logo-and-tab-bar">
        {showBadge ? (
          <LogoAndBadge>
            {logo}
            <KeyBuyerAccessBadge />
          </LogoAndBadge>
        ) : (
          logo
        )}
        <Tabs.TabBar {...tabProps} />
      </LogoAndTabBar>
      <Tabs.TabContent {...tabProps} />
      <StorefrontPolicies documents={documents} />
    </div>
  )
}
